import type { ComponentType, CSSProperties, RefObject, PointerEvent as ReactPointerEvent } from "react";
import { useState, useRef } from "react";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import type { LayoutSnapshot, SlotPlacement, WidgetKey, WidgetViewProps } from "./types";
import { useWidgetResize } from "./useWidgetResize";
import type { ResizingState, HandlePosition } from "./useWidgetResize";
import { GRID_COLS, GRID_ROW_HEIGHT } from "./types";
import { computeGlobalEmptySlots, WIDGET_REGISTRY, isWidgetLocked, partitionMobileSlots, getMobileWidgetHeight } from "./registry";
import WidgetFrame from "./WidgetFrame";
import LockedWidgetCTA from "./LockedWidgetCTA";
import { useDashboardData } from "./DashboardDataContext";

import NetPnlWidget from "./NetPnlWidget";
import ProfitFactorWidget from "./ProfitFactorWidget";
import WinRateWidget from "./WinRateWidget";
import AvgWinLossWidget from "./AvgWinLossWidget";
import CalendarWidget from "./CalendarWidget";
import MarketHoursWidget from "./MarketHoursWidget";
import RoutinesWidget from "./RoutinesWidget";
import GoalsWidget from "./GoalsWidget";
import SessionWidget from "./SessionWidget";
import EquityChartWidget from "./EquityChartWidget";
import AccountsTableWidget from "./AccountsTableWidget";
import TradesTableWidget from "./TradesTableWidget";
import AnalyticsKpisWidget from "./analytics/AnalyticsKpisWidget";
import EquityCurveWidget from "./analytics/EquityCurveWidget";
import PnlDistributionWidget from "./analytics/PnlDistributionWidget";
import MonthlyPnlWidget from "./analytics/MonthlyPnlWidget";
import SymbolPerformanceWidget from "./analytics/SymbolPerformanceWidget";
import RollingWinRateWidget from "./analytics/RollingWinRateWidget";
import HeatmapWidget from "./analytics/HeatmapWidget";
import BehavioralBiasesWidget from "./analytics/BehavioralBiasesWidget";
import SessionTimeSlotsWidget from "./analytics/SessionTimeSlotsWidget";
import SessionMoodWidget from "./analytics/SessionMoodWidget";
import SessionDurationWidget from "./analytics/SessionDurationWidget";
import SessionPnlChartWidget from "./analytics/SessionPnlChartWidget";
import OutsideSessionWidget from "./analytics/OutsideSessionWidget";
import SessionInsightWidget from "./analytics/SessionInsightWidget";
import SessionsListWidget from "./analytics/SessionsListWidget";
import LastSessionWidget from "./analytics/LastSessionWidget";
import PipStatsWidget from "./analytics/PipStatsWidget";
import PipDistributionWidget from "./analytics/PipDistributionWidget";
import PipBySymbolWidget from "./analytics/PipBySymbolWidget";
import LongShortWidget from "./analytics/LongShortWidget";
import DayOfWeekWidget from "./analytics/DayOfWeekWidget";
import PnlPerTradeWidget from "./analytics/PnlPerTradeWidget";
import NetDailyPnlWidget from "./analytics/NetDailyPnlWidget";
import RiskAdjustedWidget from "./analytics/RiskAdjustedWidget";
import DrawdownCurveWidget from "./analytics/DrawdownCurveWidget";
import HoldDurationWidget from "./analytics/HoldDurationWidget";
import ConsistencyScoreWidget from "./analytics/ConsistencyScoreWidget";
import StreakAnalysisWidget from "./analytics/StreakAnalysisWidget";
import TagStatsWidget from "./analytics/TagStatsWidget";
import SizingAnalysisWidget from "./analytics/SizingAnalysisWidget";
import PropFirmKpisWidget from "./propfirm/PropFirmKpisWidget";
import PropFirmCalendarWidget from "./propfirm/PropFirmCalendarWidget";
import PropFirmMonthRecapWidget from "./propfirm/PropFirmMonthRecapWidget";
import PropFirmReportingWidget from "./propfirm/PropFirmReportingWidget";
import PropFirmAccountsWidget from "./propfirm/PropFirmAccountsWidget";
import PropFirmPayoutsListWidget from "./propfirm/PropFirmPayoutsListWidget";
import PropFirmFiltersWidget from "./propfirm/PropFirmFiltersWidget";
import PropFirmAccountsTableWidget from "./propfirm/PropFirmAccountsTableWidget";
import PropFirmGlobalChargesWidget from "./propfirm/PropFirmGlobalChargesWidget";
import PropFirmUpcomingChargesWidget from "./propfirm/PropFirmUpcomingChargesWidget";
import PropFirmWalletBalanceWidget from "./propfirm/PropFirmWalletBalanceWidget";
import PropFirmWalletTransfersWidget from "./propfirm/PropFirmWalletTransfersWidget";
import PropFirmWalletWithdrawalsWidget from "./propfirm/PropFirmWalletWithdrawalsWidget";
import PropFirmChallengeOverviewWidget from "./propfirm/PropFirmChallengeOverviewWidget";
import NoteWidget from "./NoteWidget";
import DisciplineRoutinesWidget from "./DisciplineRoutinesWidget";
import DisciplineGoalsWidget from "./DisciplineGoalsWidget";
import NotebookBrowserWidget from "./NotebookBrowserWidget";
import MentorNotesWidget from "./mentor/MentorNotesWidget";
import { NewsFeedWidget } from "./NewsFeedWidget";
import { InterestRatesWidget } from "./InterestRatesWidget";
import { EconomicCalendarWidget } from "./EconomicCalendarWidget";
import { EarningsCalendarWidget } from "./EarningsCalendarWidget";
import ChartWidget from "./ChartWidget";

export const WIDGET_COMPONENTS: Omit<Record<WidgetKey, ComponentType<WidgetViewProps>>, "note"> & { note?: ComponentType<WidgetViewProps> } = {
  "net-pnl": NetPnlWidget,
  "profit-factor": ProfitFactorWidget,
  "win-rate": WinRateWidget,
  "avg-win-loss": AvgWinLossWidget,
  "calendar": CalendarWidget,
  "market-hours": MarketHoursWidget as ComponentType<WidgetViewProps>, // special-cased in SlotWidgetContent (takes colSpan)
  "routines": RoutinesWidget,
  "goals": GoalsWidget,
  "session": SessionWidget,
  "equity-chart": EquityChartWidget,
  "accounts-table": AccountsTableWidget,
  "trades-table": TradesTableWidget,
  "analytics-kpis": AnalyticsKpisWidget,
  "equity-curve": EquityCurveWidget,
  "pnl-distribution": PnlDistributionWidget,
  "monthly-pnl": MonthlyPnlWidget,
  "symbol-performance": SymbolPerformanceWidget,
  "rolling-win-rate": RollingWinRateWidget,
  "heatmap": HeatmapWidget,
  "behavioral-biases": BehavioralBiasesWidget,
  "session-time-slots": SessionTimeSlotsWidget,
  "session-mood": SessionMoodWidget,
  "session-duration": SessionDurationWidget,
  "session-pnl-chart": SessionPnlChartWidget,
  "outside-session": OutsideSessionWidget,
  "session-insight": SessionInsightWidget,
  "sessions-list": SessionsListWidget,
  "last-session": LastSessionWidget,
  "pip-stats": PipStatsWidget,
  "pip-distribution": PipDistributionWidget,
  "pip-by-symbol": PipBySymbolWidget,
  "long-short": LongShortWidget,
  "day-of-week": DayOfWeekWidget,
  "pnl-per-trade": PnlPerTradeWidget,
  "net-daily-pnl": NetDailyPnlWidget,
  "risk-adjusted": RiskAdjustedWidget,
  "drawdown-curve": DrawdownCurveWidget,
  "hold-duration": HoldDurationWidget,
  "consistency-score": ConsistencyScoreWidget,
  "streak-analysis": StreakAnalysisWidget,
  "tag-stats": TagStatsWidget,
  "sizing-analysis": SizingAnalysisWidget,
  "propfirm-kpis": PropFirmKpisWidget,
  "propfirm-calendar": PropFirmCalendarWidget,
  "propfirm-month-recap": PropFirmMonthRecapWidget,
  "propfirm-reporting": PropFirmReportingWidget,
  "propfirm-accounts": PropFirmAccountsWidget,
  "propfirm-payouts-list": PropFirmPayoutsListWidget,
  "propfirm-filters": PropFirmFiltersWidget,
  "propfirm-accounts-table": PropFirmAccountsTableWidget,
  "propfirm-global-charges": PropFirmGlobalChargesWidget,
  "propfirm-upcoming-charges": PropFirmUpcomingChargesWidget,
  "propfirm-wallet-balance": PropFirmWalletBalanceWidget,
  "propfirm-wallet-transfers": PropFirmWalletTransfersWidget,
  "propfirm-wallet-withdrawals": PropFirmWalletWithdrawalsWidget,
  "propfirm-challenge-overview": PropFirmChallengeOverviewWidget,
  "mentor-notes": MentorNotesWidget,
  "news-feed": NewsFeedWidget,
  "interest-rates": InterestRatesWidget,
  "economic-calendar": EconomicCalendarWidget,
  "earnings-calendar": EarningsCalendarWidget,
  "discipline-routines": DisciplineRoutinesWidget,
  "discipline-goals": DisciplineGoalsWidget,
  "notebook-browser": NotebookBrowserWidget,
  "chart": null as any, // rendered specially like "note"
  // topbar widgets are never rendered in the grid — placeholders only
  "topbar-date-filter": null as any,
  "topbar-account-filter": null as any,
  "topbar-strategy-filter": null as any,
  "topbar-add-trade": null as any,
  "topbar-add-payout": null as any,
  "topbar-pfm-filter": null as any,
  "topbar-phase-filter": null as any,
  "topbar-symbol-filter": null as any,
  "topbar-side-filter": null as any,
  "topbar-status-filter": null as any,
  "topbar-pnl-filter": null as any,
  "topbar-tags-filter": null as any,
  // "note" is rendered specially — NoteWidget requires notebookNoteId prop
};

/**
 * Resolves the content rendered inside a slot's WidgetFrame, applying the Pro gate.
 * Free users (outside mentor readOnly views) see a unified upgrade CTA instead of
 * the real Pro widget — so Pro content never leaks through legacy/imported layouts.
 */
function SlotWidgetContent({
  slot,
  onUpdateSlotConfig,
  onUpdateSlotViewMode,
}: {
  slot: SlotPlacement;
  onUpdateSlotConfig?: (slotId: string, value: string) => void;
  onUpdateSlotViewMode?: (slotId: string, mode: "chart" | "table") => void;
}) {
  const { isFreePlan, readOnly } = useDashboardData();
  if (isWidgetLocked(slot.widgetKey, isFreePlan) && !readOnly) {
    return <LockedWidgetCTA />;
  }
  if (slot.widgetKey === "note") {
    return <NoteWidget notebookNoteId={slot.notebookNoteId ?? ""} />;
  }
  if (slot.widgetKey === "chart") {
    return (
      <ChartWidget
        slotId={slot.id}
        defaultFilterValue={slot.defaultFilterValue}
        onUpdateSlotConfig={onUpdateSlotConfig ?? (() => {})}
      />
    );
  }
  if (slot.widgetKey === "market-hours") {
    return <MarketHoursWidget colSpan={slot.colSpan} />;
  }
  const Component = WIDGET_COMPONENTS[slot.widgetKey];
  return Component ? (
    <Component
      viewMode={slot.viewMode ?? "chart"}
      onSetViewMode={(mode) => onUpdateSlotViewMode?.(slot.id, mode)}
    />
  ) : null;
}

export interface GlobalDashboardGridProps {
  layout: LayoutSnapshot;
  isEditMode: boolean;
  isMobile: boolean;
  enableDnd?: boolean;
  onAddWidgetClick: (colStart: number, rowStart: number, maxColSpan: number) => void;
  onRemoveWidget: (slotId: string) => void;
  onWidgetSizeChange: (slotId: string, colSpan: number, rowSpan: number) => void;
  onMoveWidget: (slotId: string, colStart: number, rowStart: number) => void;
  onSwapWidgets: (slotIdA: string, slotIdB: string) => void;
  onUpdateSlotConfig?: (slotId: string, value: string) => void;
  onUpdateSlotViewMode?: (slotId: string, mode: "chart" | "table") => void;
}

function slotGridStyle(slot: SlotPlacement): CSSProperties {
  return {
    gridColumn: `${slot.colStart} / span ${slot.colSpan}`,
    gridRow: `${slot.rowStart} / span ${slot.rowSpan}`,
  };
}

function ResizeOverlay({
  resizingState,
  slots,
  gridStyle,
}: {
  resizingState: ResizingState;
  slots: SlotPlacement[];
  gridStyle: CSSProperties;
}) {
  const slot = slots.find((s) => s.id === resizingState.slotId);
  if (!slot) return null;

  const cells: Array<{ col: number; row: number }> = [];
  for (let c = slot.colStart; c < slot.colStart + resizingState.colSpan; c++) {
    for (let r = slot.rowStart; r < slot.rowStart + resizingState.rowSpan; r++) {
      cells.push({ col: c, row: r });
    }
  }

  return (
    <div
      style={{
        ...gridStyle,
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        zIndex: 10,
      }}
    >
      {cells.map(({ col, row }) => (
        <div
          key={`${col}-${row}`}
          style={{
            gridColumn: `${col} / span 1`,
            gridRow: `${row} / span 1`,
          }}
          className={[
            "rounded-lg border transition-colors",
            resizingState.isBlocked
              ? "bg-destructive/20 border-destructive"
              : "bg-primary/20 border-primary",
          ].join(" ")}
        />
      ))}
    </div>
  );
}

function DraggableSlot({
  slot, slots, isEditMode, isMobile, onRemove, onSizeChange, onResizingChange, gridRef, onUpdateSlotConfig, onUpdateSlotViewMode,
}: {
  slot: SlotPlacement; slots: SlotPlacement[]; isEditMode: boolean; isMobile: boolean;
  onRemove: () => void; onSizeChange: (colSpan: number, rowSpan: number) => void;
  onResizingChange: (state: ResizingState | null) => void;
  gridRef: RefObject<HTMLElement | null>;
  onUpdateSlotConfig?: (slotId: string, value: string) => void;
  onUpdateSlotViewMode?: (slotId: string, mode: "chart" | "table") => void;
}) {
  const id = `widget|${slot.id}`;
  const { setNodeRef: setDragRef, attributes, listeners, isDragging } = useDraggable({ id });
  const { setNodeRef: setDropRef, isOver } = useDroppable({ id });
  const setRef = (node: HTMLElement | null) => { setDragRef(node); setDropRef(node); };

  const reg = WIDGET_REGISTRY[slot.widgetKey];
  const [activeHandle, setActiveHandle] = useState<HandlePosition | null>(null);

  const { startResize } = useWidgetResize({
    gridRef,
    slot: {
      id: slot.id,
      colStart: slot.colStart,
      rowStart: slot.rowStart,
      colSpan: slot.colSpan,
      rowSpan: slot.rowSpan,
    },
    constraints: {
      minColSpan: reg.minColSpan,
      maxColSpan: reg.maxColSpan,
      minRowSpan: reg.minRowSpan,
      maxRowSpan: reg.maxRowSpan,
    },
    allSlots: slots.map((s) => ({
      id: s.id,
      colStart: s.colStart,
      rowStart: s.rowStart,
      colSpan: s.colSpan,
      rowSpan: s.rowSpan,
    })),
    onResizing: (state) => {
      if (state === null) setActiveHandle(null);
      onResizingChange(state);
    },
    onResizeEnd: (colSpan, rowSpan) => {
      setActiveHandle(null);
      onResizingChange(null);
      onSizeChange(colSpan, rowSpan);
    },
  });

  const handleResizeStart = (e: ReactPointerEvent<HTMLElement>, handle: HandlePosition) => {
    setActiveHandle(handle);
    startResize(e, handle);
  };

  const Component = (slot.widgetKey === "note" || slot.widgetKey === "chart") ? null : WIDGET_COMPONENTS[slot.widgetKey];
  if (slot.widgetKey !== "note" && slot.widgetKey !== "chart" && !Component) return null;

  const style: CSSProperties = {
    ...slotGridStyle(slot),
    opacity: isDragging ? 0.3 : 1,
    outline: isOver && !isDragging ? "2px solid rgb(var(--primary, 0 201 167))" : undefined,
    outlineOffset: isOver && !isDragging ? "2px" : undefined,
    minHeight: 0,
    overflow: "hidden",
  };

  return (
    <div ref={setRef} style={style} className="h-full">
      <WidgetFrame
        widgetKey={slot.widgetKey}
        currentColSpan={slot.colSpan}
        currentRowSpan={slot.rowSpan}
        gridCols={GRID_COLS}
        isEditMode={isEditMode}
        isMobile={isMobile}
        onSizeChange={onSizeChange}
        onRemove={onRemove}
        dragHandleProps={{ ...attributes, ...listeners }}
        onResizeStart={handleResizeStart}
        activeResizeHandle={activeHandle}
      >
        <SlotWidgetContent slot={slot} onUpdateSlotConfig={onUpdateSlotConfig} onUpdateSlotViewMode={onUpdateSlotViewMode} />
      </WidgetFrame>
    </div>
  );
}

function PlainSlot({
  slot, slots, isEditMode, isMobile, onRemove, onSizeChange, onResizingChange, gridRef, onUpdateSlotConfig, onUpdateSlotViewMode,
}: {
  slot: SlotPlacement; slots: SlotPlacement[]; isEditMode: boolean; isMobile: boolean;
  onRemove: () => void; onSizeChange: (colSpan: number, rowSpan: number) => void;
  onResizingChange: (state: ResizingState | null) => void;
  gridRef: RefObject<HTMLElement | null>;
  onUpdateSlotConfig?: (slotId: string, value: string) => void;
  onUpdateSlotViewMode?: (slotId: string, mode: "chart" | "table") => void;
}) {
  const reg = WIDGET_REGISTRY[slot.widgetKey];
  const [activeHandle, setActiveHandle] = useState<HandlePosition | null>(null);

  const { startResize } = useWidgetResize({
    gridRef,
    slot: {
      id: slot.id,
      colStart: slot.colStart,
      rowStart: slot.rowStart,
      colSpan: slot.colSpan,
      rowSpan: slot.rowSpan,
    },
    constraints: {
      minColSpan: reg.minColSpan,
      maxColSpan: reg.maxColSpan,
      minRowSpan: reg.minRowSpan,
      maxRowSpan: reg.maxRowSpan,
    },
    allSlots: slots.map((s) => ({
      id: s.id,
      colStart: s.colStart,
      rowStart: s.rowStart,
      colSpan: s.colSpan,
      rowSpan: s.rowSpan,
    })),
    onResizing: (state) => {
      if (state === null) setActiveHandle(null);
      onResizingChange(state);
    },
    onResizeEnd: (colSpan, rowSpan) => {
      setActiveHandle(null);
      onResizingChange(null);
      onSizeChange(colSpan, rowSpan);
    },
  });

  const handleResizeStart = (e: ReactPointerEvent<HTMLElement>, handle: HandlePosition) => {
    setActiveHandle(handle);
    startResize(e, handle);
  };

  const Component = (slot.widgetKey === "note" || slot.widgetKey === "chart") ? null : WIDGET_COMPONENTS[slot.widgetKey];
  if (slot.widgetKey !== "note" && slot.widgetKey !== "chart" && !Component) return null;

  return (
    <div style={{ ...slotGridStyle(slot), overflow: "hidden" }} className="h-full">
      <WidgetFrame
        widgetKey={slot.widgetKey}
        currentColSpan={slot.colSpan}
        currentRowSpan={slot.rowSpan}
        gridCols={GRID_COLS}
        isEditMode={isEditMode}
        isMobile={isMobile}
        onSizeChange={onSizeChange}
        onRemove={onRemove}
        onResizeStart={handleResizeStart}
        activeResizeHandle={activeHandle}
      >
        <SlotWidgetContent slot={slot} onUpdateSlotConfig={onUpdateSlotConfig} onUpdateSlotViewMode={onUpdateSlotViewMode} />
      </WidgetFrame>
    </div>
  );
}

function DroppableEmptyCell({ col, row, onClick }: { col: number; row: number; onClick: () => void }) {
  const id = `empty|${col}|${row}`;
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      style={{
        gridColumn: `${col} / span 1`,
        gridRow: `${row} / span 1`,
        outline: isOver ? "2px solid rgb(var(--primary, 0 201 167))" : undefined,
        outlineOffset: isOver ? "2px" : undefined,
        borderRadius: 8,
      }}
      className="flex items-center justify-center border-2 border-dashed border-border/50 rounded-xl cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors"
      onClick={onClick}
    >
      <div className="flex flex-col items-center gap-1 text-muted-foreground/60">
        <span className="text-2xl leading-none">+</span>
        <span className="text-xs">Ajouter un widget</span>
      </div>
    </div>
  );
}

/**
 * Remove slots that overlap with an already-placed slot.
 * Processes slots in order — first slot wins. Prevents CSS Grid visual overlap.
 */
function resolveOverlaps(slots: SlotPlacement[]): SlotPlacement[] {
  const occupied = new Set<string>();
  const result: SlotPlacement[] = [];
  for (const s of slots) {
    let overlaps = false;
    for (let c = s.colStart; c < s.colStart + s.colSpan; c++) {
      for (let r = s.rowStart; r < s.rowStart + s.rowSpan; r++) {
        if (occupied.has(`${c},${r}`)) { overlaps = true; break; }
      }
      if (overlaps) break;
    }
    if (overlaps) continue;
    for (let c = s.colStart; c < s.colStart + s.colSpan; c++) {
      for (let r = s.rowStart; r < s.rowStart + s.rowSpan; r++) occupied.add(`${c},${r}`);
    }
    result.push(s);
  }
  return result;
}

/** Build a per-row height string for gridTemplateRows, respecting each widget's rowHeight. */
function buildGridTemplateRows(slots: SlotPlacement[], totalRows: number): string {
  const rowHeights: number[] = Array(totalRows).fill(GRID_ROW_HEIGHT);
  for (const s of slots) {
    const reg = WIDGET_REGISTRY[s.widgetKey];
    if (!reg) continue;
    // A widget spanning multiple rows: its rowHeight applies to each row unit it occupies.
    for (let r = s.rowStart; r < s.rowStart + s.rowSpan; r++) {
      if (r <= totalRows) rowHeights[r - 1] = Math.max(rowHeights[r - 1], reg.rowHeight);
    }
  }
  return rowHeights.map((h) => `${h}px`).join(" ");
}

export default function GlobalDashboardGrid({
  layout, isEditMode, isMobile, enableDnd = false,
  onAddWidgetClick, onRemoveWidget, onWidgetSizeChange, onUpdateSlotConfig, onUpdateSlotViewMode,
}: GlobalDashboardGridProps) {
  const gridRef = useRef<HTMLDivElement>(null);
  const [resizingState, setResizingState] = useState<ResizingState | null>(null);

  const slots = resolveOverlaps(layout.slots);
  const maxRow = slots.reduce((m, s) => Math.max(m, s.rowStart + s.rowSpan - 1), 1);
  const totalRows = isEditMode ? maxRow + 1 : maxRow;

  const gridStyle: CSSProperties = {
    display: "grid",
    gridTemplateColumns: `repeat(${GRID_COLS}, minmax(0, 1fr))`,
    gridTemplateRows: buildGridTemplateRows(slots, totalRows),
    gap: "1rem",
  };

  if (isMobile) {
    // Render a single slot's widget inside a frame (shared by KPI + stacked).
    const renderSlot = (slot: SlotPlacement, className?: string, capHeight?: boolean) => {
      const Component = (slot.widgetKey === "note" || slot.widgetKey === "chart") ? null : WIDGET_COMPONENTS[slot.widgetKey];
      if (slot.widgetKey !== "note" && slot.widgetKey !== "chart" && !Component) return null;
      // Sans hauteur explicite, un widget à liste s'étire indéfiniment sur
      // mobile et son scroll interne ne s'active jamais (cf. registry).
      const height = capHeight ? getMobileWidgetHeight(slot.widgetKey) : undefined;
      return (
        <div
          key={slot.id}
          className={`min-w-0${className ? ` ${className}` : ""}`}
          style={height ? { height } : undefined}
        >
        <WidgetFrame
          widgetKey={slot.widgetKey}
          currentColSpan={slot.colSpan}
          currentRowSpan={slot.rowSpan}
          gridCols={GRID_COLS}
          isEditMode={isEditMode}
          isMobile={true}
          onSizeChange={(c, r) => onWidgetSizeChange(slot.id, c, r)}
          onRemove={() => onRemoveWidget(slot.id)}
        >
          <SlotWidgetContent slot={slot} onUpdateSlotConfig={onUpdateSlotConfig} onUpdateSlotViewMode={onUpdateSlotViewMode} />
        </WidgetFrame>
        </div>
      );
    };

    // In edit mode keep the simple full-width stack (drag/resize off on mobile anyway).
    if (isEditMode) {
      return <div className="flex flex-col gap-4">{slots.map((s) => renderSlot(s))}</div>;
    }

    // View mode → Pro Terminal mobile: compact KPI metrics in a 2-col grid at
    // the top, then the larger widgets stacked full-width, preserving order.
    const { kpis, fullWidth } = partitionMobileSlots(slots);

    return (
      <div className="flex flex-col gap-4">
        {kpis.length > 0 && (
          <div className="grid grid-cols-2 gap-3">
            {kpis.map((s, i) =>
              // KPI orphelin en fin de grille → il occupe la ligne entière
              // plutôt que de laisser une demi-ligne vide.
              renderSlot(s, i === kpis.length - 1 && kpis.length % 2 === 1 ? "col-span-2" : undefined),
            )}
          </div>
        )}
        {fullWidth.map((s) => renderSlot(s, undefined, true))}
      </div>
    );
  }

  const emptyCells = isEditMode ? computeGlobalEmptySlots(slots) : [];

  return (
    <div ref={gridRef} style={{ ...gridStyle, position: "relative" }}>
      {slots.map((slot) =>
        enableDnd ? (
          <DraggableSlot
            key={slot.id}
            slot={slot}
            slots={slots}
            gridRef={gridRef}
            isEditMode={isEditMode}
            isMobile={isMobile}
            onRemove={() => onRemoveWidget(slot.id)}
            onSizeChange={(c, r) => onWidgetSizeChange(slot.id, c, r)}
            onResizingChange={setResizingState}
            onUpdateSlotConfig={onUpdateSlotConfig}
            onUpdateSlotViewMode={onUpdateSlotViewMode}
          />
        ) : (
          <PlainSlot
            key={slot.id}
            slot={slot}
            slots={slots}
            gridRef={gridRef}
            isEditMode={isEditMode}
            isMobile={isMobile}
            onRemove={() => onRemoveWidget(slot.id)}
            onSizeChange={(c, r) => onWidgetSizeChange(slot.id, c, r)}
            onResizingChange={setResizingState}
            onUpdateSlotConfig={onUpdateSlotConfig}
            onUpdateSlotViewMode={onUpdateSlotViewMode}
          />
        )
      )}

      {emptyCells.map(({ col, row }) => {
        const isOccupied = (c: number) => slots.some(
          (s) => c >= s.colStart && c < s.colStart + s.colSpan &&
                 row >= s.rowStart && row < s.rowStart + s.rowSpan
        );
        let maxSpan = 0;
        for (let c = col; c <= GRID_COLS; c++) {
          if (isOccupied(c)) break;
          maxSpan++;
        }
        return (
          <DroppableEmptyCell
            key={`empty-${col}-${row}`}
            col={col}
            row={row}
            onClick={() => onAddWidgetClick(col, row, maxSpan)}
          />
        );
      })}

      {resizingState && (
        <ResizeOverlay
          resizingState={resizingState}
          slots={slots}
          gridStyle={gridStyle}
        />
      )}
    </div>
  );
}
